import { Box, Button, Typography } from "@outschool/backpack";
import { RecordingAvailability } from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import { joinCommasAnd } from "@outschool/text";
import * as Time from "@outschool/time";
import { useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  MeetingPostFragments,
  MeetingPost_ActivityFragment,
  MeetingPost_LearnerFragment,
  MeetingPost_MeetingFragment,
  MeetingPost_SectionFragment,
  RequestMeetingRecordingsShown,
  RequestMeetingRecordingsShownMutation,
  RequestMeetingRecordingsShownMutationVariables,
  SetMeetingRecordingsShown,
  SetMeetingRecordingsShownMutation,
  SetMeetingRecordingsShownMutationVariables,
} from "@outschool/ui-components-classroom";
import {
  CollapsedText,
  Container,
  Modal,
  Space,
  TextArea,
} from "@outschool/ui-legacy-component-library";
import { Screen, useBooleanState } from "@outschool/ui-utils";
import { sortBy } from "lodash";
import React from "react";
import { useHistory } from "react-router";

import * as Env from "../../../shared/Env";
import * as Routes from "../../../shared/Routes";
import * as User from "../../../shared/User";
import ErrorMessage from "../ErrorMessage";

function HideRecordingButton({
  meeting,
}: {
  meeting: MeetingPost_MeetingFragment;
}) {
  const [isModalShown, setIsModalShown] = React.useState(false);
  const [reasonToHide, setReasonToHide] = React.useState("");
  const [validationErrorMessage, setValidationErrorMessage] =
    // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
    React.useState<string>(null);
  const [mutation, { loading, error }] = useMutation<
    SetMeetingRecordingsShownMutation,
    SetMeetingRecordingsShownMutationVariables
  >(SetMeetingRecordingsShown);
  const combinedError = validationErrorMessage || error;
  const hideModal = () => {
    setIsModalShown(false);
    setReasonToHide("");
    // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
    setValidationErrorMessage(null);
  };
  const handleSubmit = React.useCallback(async () => {
    if (!reasonToHide || reasonToHide.length === 0) {
      setValidationErrorMessage("You must enter a reason");
    } else {
      await mutation({
        variables: {
          meetingUid: meeting.uid,
          isShown: false,
          reason: reasonToHide,
        },
      });
      hideModal();
    }
  }, [mutation, meeting.uid, reasonToHide]);
  const { t } = useTranslation("client\\components\\classroom\\MeetingPost");
  return (
    <React.Fragment>
      <Button
        sx={{
          marginTop: "0.25em",
        }}
        onClick={() => setIsModalShown(true)}
      >
        {t("Hide recording")}
      </Button>
      <Modal open={isModalShown} onClose={hideModal}>
        <Typography
          variant="h3"
          sx={{
            marginBottom: "1em",
          }}
        >
          {t("Hide recording")}
        </Typography>
        <Box
          sx={{
            marginBottom: "1em",
          }}
        >
          {t(
            "Please tell Outschool why you are choosing not to share this recording",
          )}
        </Box>
        <ErrorMessage
          value={combinedError}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
        <Box
          sx={{
            marginBottom: "1em",
          }}
        >
          <TextArea
            maxRows={10}
            showCount={false}
            value={reasonToHide}
            placeholder={t("Enter a reason (visible only to Outschool)")}
            onChange={e =>
              setReasonToHide((e.target as HTMLTextAreaElement).value)
            }
          />
        </Box>
        <Box
          flex
          sx={{
            justifyContent: "space-between",
          }}
        >
          <Button variant="contained" disabled={loading} onClick={handleSubmit}>
            {t("Hide recording")}
          </Button>
          <Button onClick={hideModal}>{t("Cancel")}</Button>
        </Box>
      </Modal>
    </React.Fragment>
  );
}

function ShowRecordingButton({
  meeting,
}: {
  meeting: MeetingPost_MeetingFragment;
}) {
  const [mutation, { loading }] = useMutation<
    SetMeetingRecordingsShownMutation,
    SetMeetingRecordingsShownMutationVariables
  >(SetMeetingRecordingsShown, {
    variables: {
      meetingUid: meeting.uid,
      isShown: true,
    },
  });
  const { t } = useTranslation("client\\components\\classroom\\MeetingPost");
  return (
    <Button disabled={loading} onClick={() => mutation()}>
      {t("Share recording")}
    </Button>
  );
}

function RequestRecordingButton({
  activity,
  meeting,
  learner,
}: {
  activity: MeetingPost_ActivityFragment;
  meeting: MeetingPost_MeetingFragment;
  learner: MeetingPost_LearnerFragment;
}) {
  const { t } = useTranslation("client\\components\\classroom\\MeetingPost");
  const hasUserRequested =
    meeting.recordingAvailability === RecordingAvailability.Requested;
  const lastUserRequest = hasUserRequested
    ? meeting.recordingRequests.find(
        request => request.requestedBy.uid === learner.uid,
      )
    : null;
  const defaultMessage = (lastUserRequest && lastUserRequest.message) || "";
  const [isModalShown, setIsModalShown] = React.useState(false);
  // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
  const [requestMessage, setRequestMessage] = React.useState<string>(null);
  const [validationErrorMessage, setValidationErrorMessage] =
    // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
    React.useState<string>(null);
  const [mutation, { loading, error }] = useMutation<
    RequestMeetingRecordingsShownMutation,
    RequestMeetingRecordingsShownMutationVariables
  >(RequestMeetingRecordingsShown);
  const combinedError = validationErrorMessage || error;
  const hideModal = () => {
    setIsModalShown(false);
    // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
    setRequestMessage(null);
    // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
    setValidationErrorMessage(null);
  };
  const message = requestMessage !== null ? requestMessage : defaultMessage;
  const handleSubmit = React.useCallback(async () => {
    await mutation({
      variables: {
        learnerUid: learner.uid,
        meetingUid: meeting.uid,
        message: message || undefined,
      },
    });
    hideModal();
  }, [mutation, meeting.uid, message, learner.uid]);
  return (
    <React.Fragment>
      <Button onClick={() => setIsModalShown(true)}>
        {!hasUserRequested
          ? t("Request recording")
          : t("Edit recording request")}
      </Button>
      <Modal open={isModalShown} onClose={hideModal}>
        <Typography
          variant="h3"
          sx={{
            marginBottom: "1em",
          }}
        >
          {t("Message to {{leaderName}}", {
            leaderName: User.shortenedLeaderName(activity.leader.name),
          })}
        </Typography>
        <Box
          sx={{
            marginBottom: "0.5em",
          }}
        >
          {t("Optional: tell your teacher why you find recordings useful")}
        </Box>
        <ErrorMessage
          value={combinedError}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
        <Box
          sx={{
            marginBottom: "1em",
          }}
        >
          <TextArea
            maxRows={10}
            showCount={false}
            value={message}
            placeholder={t("Enter a message")}
            onChange={e =>
              setRequestMessage((e.target as HTMLTextAreaElement).value)
            }
          />
        </Box>
        <Box
          flex
          sx={{
            justifyContent: "space-between",
          }}
        >
          <Button variant="contained" disabled={loading} onClick={handleSubmit}>
            {!hasUserRequested
              ? t("Request recording")
              : t("Save recording request")}
          </Button>
          <Button onClick={hideModal}>{t("Cancel")}</Button>
        </Box>
      </Modal>
    </React.Fragment>
  );
}

function MeetingPostActionButton({
  activity,
  meeting,
  learner,
  canManage,
}: {
  activity: MeetingPost_ActivityFragment;
  meeting: MeetingPost_MeetingFragment;
  learner: MeetingPost_LearnerFragment;
  canManage: boolean;
}) {
  const recordingAvailability = meeting && meeting.recordingAvailability;
  const canRequestOrShowRecording = canDisplayShowOrRequestRecordingButton(
    recordingAvailability,
  );
  return canManage ? (
    canRequestOrShowRecording ? (
      <ShowRecordingButton meeting={meeting} />
    ) : canDisplayHideRecordingButton(recordingAvailability) ? (
      <HideRecordingButton meeting={meeting} />
    ) : null
  ) : canRequestOrShowRecording ? (
    <RequestRecordingButton
      learner={learner}
      activity={activity}
      meeting={meeting}
    />
  ) : null;
}

function MeetingPostRecordingsDescription({
  activity,
  meeting,
  hasAnyRecordings,
}: {
  activity: MeetingPost_ActivityFragment;
  meeting: MeetingPost_MeetingFragment;
  hasAnyRecordings: boolean;
}) {
  const { t } = useTranslation("client\\components\\classroom\\MeetingPost");
  if (hasAnyRecordings) {
    return null;
  }

  const recordingAvailability = meeting?.recordingAvailability;
  const element =
    recordingAvailability === RecordingAvailability.Shown ? (
      <React.Fragment>{t("No recordings available.")}</React.Fragment>
    ) : recordingAvailability === RecordingAvailability.Processing ? (
      <React.Fragment>
        {t("Recording will appear here once it’s available.")}
      </React.Fragment>
    ) : recordingAvailability === RecordingAvailability.Requested ? (
      <React.Fragment>{t("You have requested the recording.")}</React.Fragment>
    ) : recordingAvailability === RecordingAvailability.Hidden ? (
      <React.Fragment>
        {t("{{leaderName}} chose not to share this recording.", {
          leaderName: User.shortenedLeaderName(activity.leader.name),
        })}
      </React.Fragment>
    ) : null;

  return !element ? null : (
    <Box
      data-test-id="MeetingPost-recordings"
      sx={{
        marginTop: "0.25em",
      }}
    >
      {element}
    </Box>
  );
}

function MeetingPostAttendance({
  meeting,
  section,
}: {
  meeting: MeetingPost_MeetingFragment;
  section: MeetingPost_SectionFragment;
}) {
  const { t } = useTranslation("client\\components\\classroom\\MeetingPost");
  const recordingAvailability = meeting && meeting.recordingAvailability;
  const isRecordingUnavailable =
    recordingAvailability === RecordingAvailability.Unavailable;

  const sortedLearners = sortBy(meeting.attendances || [], l => l.learner.name);

  const attendances = isRecordingUnavailable
    ? sortedLearners
    : [{ teacher: { name: section?.leader?.name } }, ...sortedLearners];

  const learnerNames =
    attendances?.length > 0
      ? joinCommasAnd(
          // @ts-ignore TS(2345): Argument of type '(string | null)[]' is not assign... Remove this comment to see the full error message
          attendances.map(attendance =>
            // https://www.typescriptlang.org/docs/handbook/advanced-types.html#intersection-types
            "learner" in attendance
              ? attendance.learner.name
              : attendance.teacher.name,
          ),
        )
      : "";
  return (
    <React.Fragment>
      {attendances.length > 0
        ? isRecordingUnavailable
          ? t("{{learnerNames}} joined, but the teacher did not.", {
              learnerNames,
            })
          : t("{{learnerNames}} joined.", { learnerNames })
        : t("No one joined.")}
    </React.Fragment>
  );
}

function ViewRecordingButton({ url }: { url: string }) {
  const history = useHistory();
  const { t } = useTranslation("client\\components\\classroom\\MeetingPost");
  return (
    <Button onClick={() => history.push(url)}>{t("View recording")}</Button>
  );
}

function MeetingPostRequests({
  meeting,
  canManage,
}: {
  meeting: MeetingPost_MeetingFragment;
  canManage: boolean;
}) {
  const { userTimeZone } = useSession();
  const { t } = useTranslation("client\\components\\classroom\\MeetingPost");
  const recordingAvailability = meeting && meeting.recordingAvailability;
  const canSeeRequests =
    canManage && recordingAvailability !== RecordingAvailability.Shown;
  if (!canSeeRequests) {
    return null;
  }
  const requests =
    meeting.recordingRequests.length > 0 ? (
      <React.Fragment>
        {meeting.recordingRequests.map(request => (
          <Box
            key={request.requestedBy.uid}
            sx={{
              marginBottom: "1em",
            }}
          >
            <Box
              sx={{
                color: "grey.500",
              }}
            >
              {Time.formatDateTimeWithFullWeekday(
                request.requestedAt,
                // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                userTimeZone,
              )}
            </Box>
            <Box
              sx={{
                fontWeight: "fontWeightBold",
              }}
            >
              {t("{{requesterName}} requested recording", {
                requesterName: request.requestedBy.name,
              })}
            </Box>
            <Space y="small" />
            <CollapsedText
              showMoreLabel={t("Show More...")}
              showLessLabel={t("Show Less...")}
              maxCharacters={180}
            >
              {request.message}
            </CollapsedText>
          </Box>
        ))}
      </React.Fragment>
    ) : null;
  return (
    <Box
      data-test-id="MeetingPost-requests"
      sx={{
        marginTop: "0.5em",
        marginLeft: "1em",
      }}
    >
      {requests}
    </Box>
  );
}

export default function MeetingPost({
  activity,
  section,
  meeting,
  learner,
  canManage,
  isNew,
}: {
  activity: MeetingPost_ActivityFragment;
  section: MeetingPost_SectionFragment;
  meeting: MeetingPost_MeetingFragment;
  learner: MeetingPost_LearnerFragment;
  canManage: boolean;
  isNew?: boolean;
}) {
  const { userTimeZone } = useSession();
  const isMobile = Screen.useIsMobile();
  const { t } = useTranslation("client\\components\\classroom\\MeetingPost");

  const recordingAvailability = meeting?.recordingAvailability;

  const isRecordingUnavailable =
    recordingAvailability === RecordingAvailability.Unavailable;

  const postTitle = Time.createDayjs(meeting.start_time).isAfter()
    ? t("Live meeting is starting soon")
    : Time.createDayjs(meeting.end_time).isAfter()
    ? t("Live meeting is in progress")
    : isRecordingUnavailable
    ? t("Scheduled meeting did not happen")
    : t("Class met over video chat");

  const hasAnyRecordings = meeting?.classRecordings?.length > 0;
  const meetingHasEnded = Time.createDayjs().isAfter(
    Time.createDayjs(meeting.end_time),
  );

  const [highlight, _setHighlight, clearHighlight] = useBooleanState(isNew);
  React.useEffect(() => {
    if (highlight) {
      const timer = setTimeout(() => {
        clearHighlight();
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [highlight, clearHighlight]);

  return (
    <Container
      size="small"
      sx={{
        paddingX: "small",
        marginBottom: "large",
        backgroundColor: highlight ? "yellow5" : undefined,
        transition: "background-color 2s ease",
      }}
    >
      <Box
        flex
        sx={{
          flexDirection: "column",
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: "row",
            marginBottom: "0.5em",
            alignItems: "start",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <Box flex>
            <Box>
              <Box
                sx={{
                  color: "grey.500",
                }}
              >
                {Time.formatDateTimeWithFullWeekday(
                  meeting.start_time,
                  // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                  userTimeZone,
                )}
              </Box>
              <Box
                sx={{
                  fontWeight: "fontWeightBold",
                }}
                data-test-id="MeetingPost-title"
              >
                {postTitle}
              </Box>
            </Box>
          </Box>
          {!isMobile && (
            <Box>
              {meetingHasEnded && hasAnyRecordings && (
                <>
                  {meeting.classRecordings.map(recording => (
                    <Box key={recording.uid}>
                      <ViewRecordingButton
                        url={Routes.recordingPath(section.uid, recording.uid)}
                      />
                    </Box>
                  ))}
                </>
              )}
              <MeetingPostActionButton
                activity={activity}
                meeting={meeting}
                learner={learner}
                canManage={canManage}
              />
            </Box>
          )}
        </Box>
        {meetingHasEnded && (
          <React.Fragment>
            <Box data-test-id="MeetingPost-attendance">
              <MeetingPostAttendance meeting={meeting} section={section} />
            </Box>
            <MeetingPostRecordingsDescription
              activity={activity}
              meeting={meeting}
              hasAnyRecordings={hasAnyRecordings}
            />
            <MeetingPostRequests meeting={meeting} canManage={canManage} />
          </React.Fragment>
        )}
        {isMobile && (
          <Box>
            {meetingHasEnded && hasAnyRecordings && (
              <>
                {meeting.classRecordings.map(recording => (
                  <Box key={recording.uid}>
                    <ViewRecordingButton
                      url={Routes.recordingPath(section.uid, recording.uid)}
                    />
                  </Box>
                ))}
              </>
            )}
            <MeetingPostActionButton
              activity={activity}
              meeting={meeting}
              learner={learner}
              canManage={canManage}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
}

MeetingPost.fragments = MeetingPostFragments;

// When a teacher can "Show" a recording, a parent can "request" it.
function canDisplayShowOrRequestRecordingButton(
  recordingAvailability: $TSFixMe,
) {
  return (
    recordingAvailability === RecordingAvailability.Hidden ||
    recordingAvailability === RecordingAvailability.Requestable ||
    recordingAvailability === RecordingAvailability.Requested
  );
}

// Once a recording is shown, a teacher can hide it. Parents don't need
// an action button in this case.
function canDisplayHideRecordingButton(recordingAvailability: $TSFixMe) {
  return (
    !canDisplayShowOrRequestRecordingButton(recordingAvailability) &&
    recordingAvailability === RecordingAvailability.Shown
  );
}

export function MeetingPostTeacherPreview({ meeting }: $TSFixMe) {
  const { t } = useTranslation("client\\components\\classroom\\MeetingPost");
  const recordingAvailability = meeting && meeting.recordingAvailability;
  if (canDisplayShowOrRequestRecordingButton(recordingAvailability)) {
    return (
      <Box
        sx={{
          marginBottom: "1em",
        }}
      >
        <Typography variant="h3">{t("Share in classroom")}</Typography>
        {t(
          'This recording was made during a scheduled class meeting. The following meeting post already appears in the classroom. Press "Share recording" below to include a link to the recording in the meeting post.',
        )}
      </Box>
    );
  } else if (canDisplayHideRecordingButton(recordingAvailability)) {
    return (
      <Box
        sx={{
          marginBottom: "1em",
        }}
      >
        <Typography variant="h3">{t("Shared in classroom")}</Typography>
        {t(
          "This recording was made during a scheduled class meeting. The following meeting post already appears in the classroom and includes a link to the recording.",
        )}
      </Box>
    );
  } else {
    return (
      <Box
        sx={{
          marginBottom: "1em",
        }}
      >
        <Typography variant="h3">{t("Classroom preview")}</Typography>
        {t(
          "This recording was made during a scheduled class meeting. The meeting appears in the classroom like this:",
        )}
      </Box>
    );
  }
}
